"use client";

// ============================================================
// CLUBHUB – Mein Bereich: freigegebene Dokumente
// ============================================================
// Nur was fuer Mitglieder oder eine eigene Gruppe freigegeben ist
// (die Auswahl trifft `lib/portal/daten.ts`). Nach Ordnern, mit
// Suche, Neues markiert. Herunterladen ueber eine kurzlebige
// Adresse — mit Sitzung `/api/dokumente/<id>` (Rechte wie in der Ablage)
// (Document.storageKey), Zugriff protokolliert.
// ============================================================
import { useMemo, useState } from "react";
import { Download, FileImage, FileSpreadsheet, FileText, FolderOpen, Users } from "lucide-react";
import { EmptyState } from "@/components/ui/empty-state";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/toast";
import { TonBadge } from "@/components/mitglieder/status-badge";
import { formatDate } from "@/lib/utils";
import type { PortalDocument } from "@/lib/portal/views";

function Zeichen({ mime }: { mime: string }) {
  const Icon = mime.startsWith("image/") ? FileImage : mime.includes("sheet") ? FileSpreadsheet : FileText;
  return (
    <span className="bg-bg-secondary text-text-tertiary grid h-10 w-10 shrink-0 place-items-center rounded-xl">
      <Icon className="h-5 w-5" aria-hidden />
    </span>
  );
}

function art(mime: string): string {
  if (mime === "application/pdf") return "PDF";
  if (mime.startsWith("image/")) return "Bild";
  if (mime.includes("sheet")) return "Tabelle";
  if (mime.includes("word")) return "Word";
  return "Datei";
}

function groesse(bytes: number): string {
  return bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1).replace(".", ",")} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

export function Dokumente({ dokumente, name }: { dokumente: PortalDocument[]; name: string }) {
  const [q, setQ] = useState("");
  const treffer = useMemo(() => {
    const s = q.trim().toLowerCase();
    return s ? dokumente.filter((d) => [d.name, d.description, d.folder].some((f) => f?.toLowerCase().includes(s))) : dokumente;
  }, [dokumente, q]);
  const ordner = [...new Set(treffer.map((d) => d.folder))];

  if (dokumente.length === 0) {
    return <EmptyState icon={FolderOpen} title="Noch nichts freigegeben" description={`Sobald der Verein Dokumente für Mitglieder oder die Gruppen von ${name} freigibt, stehen sie hier.`} />;
  }

  return (
    <div className="space-y-5">
      <Input type="search" placeholder="Dokument suchen …" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Dokumente durchsuchen" wrapperClassName="max-w-md" />
      {treffer.length === 0 && <EmptyState title="Nichts gefunden" description="Anderer Suchbegriff? Gesucht wird in Name, Beschreibung und Ordner." />}
      {ordner.map((o) => {
        const liste = treffer.filter((d) => d.folder === o);
        const gruppe = liste[0]?.groupName;
        return (
          <section key={o} aria-label={o} className="space-y-2">
            <h2 className="text-body-sm text-text-secondary flex items-center gap-2 font-medium">
              <FolderOpen className="text-text-tertiary h-4 w-4" aria-hidden />
              {o}
              <span className="text-caption text-text-tertiary flex items-center gap-1 font-normal">
                {gruppe ? (
                  <>
                    <Users className="h-3 w-3" aria-hidden /> nur {gruppe}
                  </>
                ) : (
                  "· für alle Mitglieder"
                )}
              </span>
            </h2>
            <ul className="bg-bg-elevated shadow-e2 divide-border-subtle divide-y rounded-2xl ring-1 ring-black/[0.04] dark:ring-white/[0.06]">
              {liste.map((d) => (
                <li key={d.id} className="flex items-center gap-3 px-4 py-3">
                  <Zeichen mime={d.mimeType} />
                  <div className="min-w-0 flex-1">
                    <p className="text-body-md text-text-primary flex items-center gap-2">
                      <span className="truncate">{d.name}</span>
                      {d.isNew && <TonBadge ton="accent">Neu</TonBadge>}
                    </p>
                    <p className="text-caption text-text-tertiary truncate tabular-nums">
                      {[d.description, `${art(d.mimeType)} · ${groesse(d.size)}`, formatDate(d.updatedAt)].filter(Boolean).join(" · ")}
                    </p>
                  </div>
                  {d.href ? (
                    // Mit Sitzung: die Ablage liefert aus und prueft dieselben Rechte noch einmal.
                    <a href={d.href} download aria-label={`${d.name} herunterladen`} className="text-text-secondary hover:bg-bg-secondary hover:text-text-primary grid h-10 w-10 shrink-0 place-items-center rounded-xl">
                      <Download className="h-[18px] w-[18px]" aria-hidden />
                    </a>
                  ) : (
                    <button
                      type="button"
                      onClick={() => toast("Download startet", { description: `${d.name} (${groesse(d.size)})` })}
                      aria-label={`${d.name} herunterladen`}
                      className="text-text-secondary hover:bg-bg-secondary hover:text-text-primary grid h-10 w-10 shrink-0 place-items-center rounded-xl"
                    >
                      <Download className="h-[18px] w-[18px]" aria-hidden />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </section>
        );
      })}
    </div>
  );
}
